import React, { useState, useEffect } from 'react';
import { fetchCollection, getVal, submitWebform } from '../lib/api';

export default function OfferPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenTriggered, setHasBeenTriggered] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [interest, setInterest] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    async function loadPopup() {
      try {
        const data = await fetchCollection('tilbud_og_popups');
        if (data.items && data.items.length > 0) {
          const activePopup = data.items.find(item => item.active !== false);
          if (activePopup) {
            setPopupData(activePopup);
          }
        }
      } catch (err) {
        console.error('Fejl ved hentning af tilbud_og_popups:', err);
      }
    }
    loadPopup();
  }, []);

  useEffect(() => {
    // Check if user has already dismissed or submitted the popup in this session
    const hasDismissed = sessionStorage.getItem('jfd_offer_popup_dismissed');
    if (hasDismissed) return;

    // Show popup after 2 seconds of browsing
    const timer = setTimeout(() => {
      setHasBeenTriggered(true);
      // Small delay to allow DOM insertion before transition
      const transitionTimer = setTimeout(() => {
        setIsOpen(true);
      }, 50);
      return () => clearTimeout(transitionTimer);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('jfd_offer_popup_dismissed', 'true');
    // Remove from DOM after transition completes
    setTimeout(() => {
      setHasBeenTriggered(false);
    }, 700);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setSubmitError('');

    if (!name.trim() || !phone.trim() || !email.trim()) {
      setSubmitStatus('error');
      setSubmitError('Udfyld venligst navn, telefon og e-mail.');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        name,
        phone,
        email,
        message: interest ? `Interesseret i: ${interest}` : 'Ønsker et uforpligtende tilbud',
        website_url: websiteUrl,
      };

      await submitWebform(payload);
      setSubmitStatus('success');
      sessionStorage.setItem('jfd_offer_popup_dismissed', 'true');
      
      // Auto close after 5 seconds on success
      setTimeout(() => {
        setIsOpen(false);
        setTimeout(() => {
          setHasBeenTriggered(false);
        }, 700);
      }, 500);
    } catch (err) {
      console.error('Fejl ved indsendelse af tilbud-popup:', err);
      setSubmitStatus('error');
      setSubmitError(err.message || 'Der skete en fejl. Prøv venligst igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasBeenTriggered) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none flex justify-center p-4 md:p-6">
      <div 
        className={`relative w-full max-w-md rounded-3xl border border-brand-orange/20 bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.15)] transition-all duration-700 ease-out pointer-events-auto ${
          isOpen 
            ? 'translate-y-0 opacity-100' 
            : 'translate-y-full opacity-0'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-orange"
          aria-label="Luk tilbudsvindue"
        >
          <i className="fa-solid fa-xmark text-sm" aria-hidden="true" />
        </button>

        {submitStatus === 'success' ? (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
              <i className="fa-solid fa-check text-2xl" aria-hidden="true" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">Mange tak! 🎉</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Vi har modtaget din forespørgsel og vender tilbage til dig med et uforpligtende tilbud hurtigst muligt!
            </p>
            <button
              onClick={handleClose}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-100 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 transition-colors hover:bg-slate-200"
            >
              Luk vindue
            </button>
          </div>
        ) : (
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange">
              <i className="fa-solid fa-gift text-lg animate-bounce" aria-hidden="true" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">
                {popupData ? getVal(popupData.title) : 'Få et uforpligtende tilbud! 🥳'}
              </h3>
              {popupData?.description ? (
                <div 
                  className="text-xs text-slate-500 mt-0.5" 
                  dangerouslySetInnerHTML={{ __html: getVal(popupData.description) }} 
                />
              ) : (
                <p className="text-xs text-slate-500 mt-0.5">
                  Planlægger du en fest? Lad os sammensætte det perfekte setup.
                </p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            {/* Honeypot field */}
            <div className="sr-only absolute">
              <label htmlFor="website_url">Efterlad dette felt tomt</label>
              <input
                id="website_url"
                type="text"
                name="website_url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="popup-name" className="sr-only">Navn</label>
                <input
                  id="popup-name"
                  type="text"
                  required
                  placeholder="Dit navn *"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-900 placeholder-slate-400 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
              </div>

              <div>
                <label htmlFor="popup-phone" className="sr-only">Telefon</label>
                <input
                  id="popup-phone"
                  type="tel"
                  required
                  placeholder="Telefonnummer *"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-900 placeholder-slate-400 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
              </div>
            </div>

            <div>
              <label htmlFor="popup-email" className="sr-only">E-mail</label>
              <input
                id="popup-email"
                type="email"
                required
                placeholder="E-mailadresse *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-900 placeholder-slate-400 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-orange"
              />
            </div>

            <div>
              <label htmlFor="popup-interest" className="sr-only">Hvad skal du leje?</label>
              <input
                id="popup-interest"
                type="text"
                placeholder="Hvad skal du leje? (f.eks. telt, slushice, borde)"
                value={interest}
                onChange={(e) => setInterest(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-900 placeholder-slate-400 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-orange"
              />
            </div>

            {submitStatus === 'error' && (
              <p className="text-xs font-medium text-rose-600 bg-rose-50 rounded-lg p-2">
                {submitError}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white shadow-md transition-all duration-200 hover:bg-brand-orange-hover hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <i className="fa-solid fa-circle-notch animate-spin" aria-hidden="true" />
                  Sender...
                </>
              ) : (
                <>
                  Få et tilbud nu
                  <i className="fa-solid fa-paper-plane text-[10px]" aria-hidden="true" />
                </>
              )}
            </button>
          </form>
        </div>
      )}
      </div>
    </div>
  );
}
